import {
    Cell,
    CellChange,
    CellTemplate,
    Column,
    isAlphaNumericKey,
    isNavigationKey,
    keyCodes,
    MenuOption,
    ReactGrid,
    ReactGridProps,
    Row,
} from '@silevis/reactgrid';
import { cloneDeep, noop, uniq } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { ScrollBars2, ScrollBars2Ref } from '@meitu/saas-ui';
import { clientInfo } from '@meitu/pc-bridge';
import { nextColorGetter } from '@meitu/saas-editor-core/es/layers/chart-layer/utils/chart-color';
import { registerStyleSheet } from '@meitu/saas-editor-core/es/utils/common-kit';
import IconFont from '@meitu/xdesign-icon-font';
import { ColorGroup } from '@meitu/xdesign-widgets';
import { useLocaleStore } from '@/i18n';
import { ActionInfo } from '../../types';
import '@silevis/reactgrid/styles.css';
import './index.less';

const bizTriggerClassName = 'rg-color-picker-trigger';
const widgetColorTriggerClassName = 'widgets-color-btn';
const scrollerClassName = 'table-scroller';
const CELL_HEIGHT = 40;

type CellValue = string | number | null;

interface HeaderCell extends Cell {
    type: 'header';
    text: string;
}

interface CustomCell extends Cell {
    type: 'text-and-color' | 'text' | 'value' | 'empty';
    bizValue: CellValue;
    color?: string;
    colorIndex?: number;
}

class HeaderTemplate implements CellTemplate<HeaderCell> {
    getCompatibleCell: CellTemplate<HeaderCell>['getCompatibleCell'] = (uncertainCell) => {
        const text = uncertainCell.text ?? '';
        const value = parseFloat(text);
        return { ...uncertainCell, text, value };
    };

    render: CellTemplate<HeaderCell>['render'] = (cell) => {
        return cell.text;
    };

    isFocusable: CellTemplate<HeaderCell>['isFocusable'] = () => false;

    getClassName: CellTemplate<HeaderCell>['getClassName'] = (cell, isInEditMode) => {
        return cell.className ? cell.className : '';
    };
}

let rmStyle = noop;

class CustomTemplate implements CellTemplate<CustomCell> {
    constructor(
        private onChangeColor: (index: number, color: string) => void,
        private setIsEditing: (editing: boolean) => void,
        private clearSelections: () => void,
        private disableAutoScroll: () => void,
        private enableAutoScroll: () => void,
        private setIsDisabled: (b: boolean) => void,
    ) {}

    getClassName: CellTemplate<CustomCell>['getClassName'] = (cell) => {
        const bizValueIsError = cell.type === 'value' && isNaN(Number(cell.text));
        return (cell.className ? cell.className + ' ' : '') + (bizValueIsError ? 'rg-cell-error' : '');
    };

    getCompatibleCell: CellTemplate<CustomCell>['getCompatibleCell'] = (uncertainCell) => {
        if (uncertainCell.type === 'text') {
            const bizValue = [null, undefined].includes(uncertainCell.bizValue as any)
                ? ''
                : `${uncertainCell.bizValue}`;
            return {
                ...uncertainCell,
                text: bizValue,
                bizValue,
                value: NaN,
            };
        }
        return {
            ...uncertainCell,
            text: [null, undefined].includes(uncertainCell.bizValue as any) ? '' : `${uncertainCell.bizValue}`,
            value: typeof uncertainCell.bizValue === 'number' ? uncertainCell.bizValue : 0,
            bizValue: uncertainCell.bizValue ?? null,
        };
    };

    handleKeyDown: CellTemplate<CustomCell>['handleKeyDown'] = (cell, keyCode, ctrl, _, alt, key) => {
        if (!ctrl && !alt && isAlphaNumericKey(keyCode)) {
            return { cell: this.getCompatibleCell({ ...cell, bizValue: key }), enableEditMode: true };
        }
        return {
            cell,
            enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
        };
    };

    update: NonNullable<CellTemplate<CustomCell>['update']> = (cell, cellToMerge) => {
        const isFromClipboard = !Object.keys(cellToMerge).includes('bizValue');
        if (isFromClipboard) {
            return this.getCompatibleCell({
                ...cell,
                bizValue: cell.type === 'value' && !isNaN(cellToMerge.value) ? cellToMerge.value : cellToMerge.text,
            });
        }
        return this.getCompatibleCell({ ...cell, bizValue: cellToMerge.bizValue });
    };

    render: CellTemplate<CustomCell>['render'] = (cell, isInEditMode, onCellChanged) => {
        this.setIsEditing(isInEditMode);
        if (cell.type === 'empty') return null;
        if (isInEditMode) {
            const onChange = (value: string, commit = false) => {
                let bizValue: CellValue = value;
                if (cell.type === 'value' && value !== '' && !isNaN(Number(value))) {
                    bizValue = Number(value);
                }
                onCellChanged(this.getCompatibleCell({ ...cell, bizValue }), commit);
            };
            return (
                <input
                    ref={(input) => input?.focus()}
                    className="rg-celleditor-input"
                    defaultValue={cell.text}
                    onBlur={(e) => onChange(e.currentTarget.value, true)}
                    onChange={(e) => onChange(e.currentTarget.value)}
                    onCopy={(e) => e.stopPropagation()}
                    onCut={(e) => e.stopPropagation()}
                    onPaste={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode)) e.stopPropagation();
                    }}
                />
            );
        }
        return (
            <div className="rg-custom-inner">
                {cell.type === 'text-and-color' && !!cell.text && !!cell.color && cell.colorIndex !== undefined && (
                    <ColorGroup
                        popupTriggerSize={{
                            width: 20,
                            height: 20,
                        }}
                        color={cell.color}
                        setColor={(val) => {
                            this.onChangeColor(cell.colorIndex!, val);
                        }}
                        setColorAfter={(newColor, _, { isBrandColor }) => {
                            nextColorGetter.resetSource(newColor);
                            if (isBrandColor) {
                                window._stat_track?.('hb_layers_func_use', {
                                    func_id: '8_107',
                                });
                            }
                        }}
                        isGradient={false}
                        showIcon={false}
                        onToggleBizTab={(bizTab) => {
                            if (bizTab === 'team') {
                                console.log('bizTab', bizTab);
                            }
                        }}
                        onOpenChange={(open) => {
                            if (open) {
                                // 透过ColorGroup双击处于边缘的单元格, 会导致进入编辑态, 导致页面错位, 所以要临时禁掉编辑功能
                                this.setIsDisabled(true);
                                this.disableAutoScroll();
                                try {
                                    rmStyle();
                                } catch (err) {
                                    console.log('===error', err);
                                }
                                rmStyle = registerStyleSheet(`
                                    .rg-touch-fill-handle,
                                    .rg-partial-area-selected-range {
                                        visibility: hidden;
                                    }
                                `);
                            } else {
                                this.setIsDisabled(false);
                                this.enableAutoScroll();
                                try {
                                    rmStyle();
                                } catch (err) {
                                    console.log('===error', err);
                                }
                                this.clearSelections();
                            }
                        }}
                    >
                        {({ PopupTrigger }) => (
                            <div className={bizTriggerClassName} style={{ background: cell.color }}>
                                {PopupTrigger}
                            </div>
                        )}
                    </ColorGroup>
                )}
                <span>{cell.text}</span>
            </div>
        );
    };
}

type TableData = CellValue[][];

interface TableProps {
    fixCSSInfo: { top: number; left: number };
    data: TableData;
    colorAxis: 'categoryAxis' | 'valueAxis';
    colors: string[];
    onChangeColors: (colors: string[]) => void;
    onChangeData: (data: TableData) => void;
    onAction?: (actionInfo: ActionInfo) => void;
}

const Table: React.FC<TableProps> = ({
    fixCSSInfo,
    data,
    colorAxis,
    colors,
    onChangeColors,
    onChangeData,
    onAction,
}) => {
    const { $t } = useLocaleStore();
    const defaultWidth = 140;
    const [colWidthMap, setColWidthMap] = useState<{ [index: number]: number }>({ 0: 80 });
    const reactGridRef = useRef<ReactGrid>(null);
    const [isEditing, setIsEditing] = useState(false);
    const scrollBars2Ref = useRef<ScrollBars2Ref>(null);
    const [isDisabled, setIsDisabled] = useState(false);

    const scrollTo = (pos: { left?: number; top?: number }) => {
        if (!scrollBars2Ref.current) return;
        if (pos.left) scrollBars2Ref.current.scrollLeft = pos.left;
        if (pos.top) scrollBars2Ref.current.scrollTop = pos.top;
    };

    // reactgrid编辑时编辑框是固定位置, 不会跟随滚动走, 所以编辑时禁用滚动
    useEffect(() => {
        if (!isEditing) return;
        const rmStyle = registerStyleSheet(`
            .${scrollerClassName} {
                overflow: hidden !important;
            }
            .ScrollbarsCustom-Track,
            .ScrollbarsCustom-Thumb {
                pointer-events: none !important;
            }
        `);
        return rmStyle;
    }, [isEditing]);

    useEffect(() => {
        const onPaste = () => {
            onAction?.({ target: 'pasteData' });
        };
        document.addEventListener('paste', onPaste, true);
        return () => document.removeEventListener('paste', onPaste, true);
    }, [onAction]);

    // ReactGrid内部的bug，编辑框会偏移, 需要手动修复
    useEffect(() => {
        const rmStyle = registerStyleSheet(`
            .rg-context-menu,
            .rg-celleditor {
                transform: translate(${fixCSSInfo.left}px, ${fixCSSInfo.top}px);
            }
        `);
        return rmStyle;
    }, [fixCSSInfo]);

    useEffect(() => {
        // 对角线度数
        const angle = (Math.atan(colWidthMap[0] / CELL_HEIGHT) * 180) / Math.PI;
        const rmStyle = registerStyleSheet(`
            .rg-empty-cell::after {
                transform: rotate(-${angle}deg);
            }
        `);
        return rmStyle;
    }, [colWidthMap[0]]);

    // 将点击事件穿透到颜色触发器
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (!(e.target instanceof HTMLElement)) return;
            if (e.target.classList.contains('rg-celleditor-input')) return;

            const elements = document.elementsFromPoint(e.x, e.y);
            const clickedColorTrigger = elements.find((el) => el.classList.contains(bizTriggerClassName));

            if (clickedColorTrigger) {
                clickedColorTrigger
                    .querySelector<HTMLDivElement>(`.${bizTriggerClassName} .${widgetColorTriggerClassName}`)
                    ?.click();
                e.stopPropagation();
            }
        };
        window.addEventListener('click', handleClick);
        return () => {
            window.removeEventListener('click', handleClick);
        };
    }, []);

    const getCellTemplateInstance = () => {
        // 点击最边缘的单元格时, 或者鼠标move时, 都会自动滚动整个表格, 这里根据看reactGrid源码的逻辑, 直接hark一下
        const disableAutoScroll = () => {
            (reactGridRef.current as any)?.updateState((old) => ({
                ...old,
                scrollableElement: document.body,
            }));
        };
        const enableAutoScroll = () => {
            (reactGridRef.current as any)?.updateState((old) => ({
                ...old,
                scrollableElement: document.querySelector(`.${scrollerClassName}`),
            }));
        };
        return new CustomTemplate(
            onChangeColor,
            setIsEditing,
            reactGridRef.current?.clearSelections ?? noop,
            disableAutoScroll,
            enableAutoScroll,
            setIsDisabled,
        );
    };

    const getCapText = (idx: number) => {
        const text = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        return idx > 26 ? text[Math.floor(idx / 26) - 1] + text[idx % 26] : text[idx];
    };

    const headerRow: Row<HeaderCell> = {
        rowId: 'header',
        cells: Array(data[0].length + 1)
            .fill(null)
            .map((_, idx) => ({ type: 'header', text: idx === 0 ? '' : getCapText(idx - 1) })),
    };

    const rows = data.map<Row<HeaderCell | CustomCell>>((rowData, rowIdx) => {
        return {
            rowId: rowIdx,
            height: CELL_HEIGHT,
            cells: [
                {
                    type: 'header',
                    text: `${rowIdx + 1}`,
                },
                {
                    type: rowIdx === 0 ? 'empty' : colorAxis === 'categoryAxis' ? 'text-and-color' : 'text',
                    bizValue: rowData[0],
                    editable: !isDisabled && rowIdx === 0,
                    color: rowIdx > 0 && colorAxis === 'categoryAxis' ? colors[rowIdx - 1] : undefined,
                    colorIndex: rowIdx > 0 && colorAxis === 'categoryAxis' ? rowIdx - 1 : undefined,
                },
                ...rowData.slice(1).map<CustomCell>((value, colIdx) => {
                    return {
                        type: rowIdx === 0 ? (colorAxis === 'valueAxis' ? 'text-and-color' : 'text') : 'value',
                        bizValue: value,
                        color: rowIdx === 0 && colorAxis === 'valueAxis' ? colors[colIdx] : undefined,
                        nonEditable: isDisabled,
                        colorIndex: rowIdx === 0 && colorAxis === 'valueAxis' ? colIdx : undefined,
                    };
                }),
            ],
        };
    });

    const columns: Column[] = Array(data[0].length + 1)
        .fill(null)
        .map<Column>((_, idx) => ({
            columnId: idx === 0 ? 'header' : idx - 1,
            width: idx === 0 ? 24 : (colWidthMap[idx - 1] ?? defaultWidth),
            resizable: idx !== 0,
        }));

    const handleChanges = (changes: CellChange<CustomCell>[]) => {
        const newData = cloneDeep(data);
        changes.forEach((change) => {
            const { rowId, columnId, newCell } = change;
            newData[rowId][columnId] = newCell.bizValue;
        });
        onChangeData(newData);
    };

    const fixMenuPosition = () => {
        const menuDom = document.querySelector<HTMLDivElement>('.rg-context-menu');
        const limitDom = document.querySelector<HTMLDivElement>('.chartDataEditor');
        if (!menuDom) return;
        if (!limitDom) return;
        const menuRect = menuDom.getBoundingClientRect();
        const limitRect = limitDom.getBoundingClientRect();
        if (menuRect.bottom > limitRect.bottom) menuDom.style.top = `${limitRect.bottom - menuRect.height}px`;
        if (menuRect.right > limitRect.right) menuDom.style.left = `${limitRect.right - menuRect.width}px`;
    };

    const simpleHandleContextMenu: ReactGridProps['onContextMenu'] = (
        selectedRowIds,
        selectedColIds,
        selectionMode,
        menuOptions,
        selectedRanges,
    ) => {
        // reactgrid无法自定义menu位置, 会超出commonModal导致被隐藏一部分, 这里直接手动修正位置
        setTimeout(() => {
            fixMenuPosition();
        }, 20);
        const isSelectMultCell = selectedRanges.flat().length > 1;
        const isSelectedRow = isSelectMultCell && uniq(selectedRanges.flat().map((s) => s.rowId)).length === 1;
        const isSelectedCol = isSelectMultCell && uniq(selectedRanges.flat().map((s) => s.columnId)).length === 1;
        if (!isSelectedRow && !isSelectedCol) return [];
        const thisSelectedIndex = Number(isSelectedRow ? selectedRanges[0][0].rowId : selectedRanges[0][0].columnId);

        const addAction = (mode: 'row' | 'col', side: 'above' | 'below') => {
            onAction?.({ target: 'updateRowOrColCount' });

            const newData = cloneDeep(data);
            if (mode === 'row') {
                const newRow = [
                    `${$t((v) => v.chartLayer.data.item)}${thisSelectedIndex + (side === 'above' ? 0 : 1)}`,
                    ...Array(data[0].length - 1).fill(null),
                ];
                newData.splice(thisSelectedIndex + (side === 'below' ? 1 : 0), 0, newRow);
            } else {
                newData.forEach((row, index) => {
                    const newValue =
                        index === 0
                            ? `${$t((v) => v.chartLayer.data.series)}${thisSelectedIndex + (side === 'above' ? 0 : 1)}`
                            : null;
                    row.splice(thisSelectedIndex + (side === 'below' ? 1 : 0), 0, newValue);
                });
            }
            return newData;
        };

        const getMenuOptionNode = ({ iconName, label }: { iconName: string; label: React.ReactNode }) => (
            <div className="inner-menu-option">
                <IconFont name={iconName} fontSize={16} />
                <div className="menu-option-label">{label}</div>
            </div>
        );

        const getAddMenuOption = (mode: 'row' | 'col', side: 'above' | 'below'): MenuOption => {
            const iconName =
                side === 'above'
                    ? mode === 'row'
                        ? 'ArrowUp'
                        : 'ArrowLeft'
                    : mode === 'row'
                      ? 'ArrowDown'
                      : 'ArrowRight';
            return {
                id: `add-${mode}-${side}`,
                label: getMenuOptionNode({
                    iconName,
                    label: $t(
                        (v) =>
                            v.chartLayer.panel[
                                `addOne${mode === 'row' ? 'Row' : 'Col'}On${side === 'above' ? 'Above' : 'Below'}`
                            ],
                    ),
                }),
                handler: () => {
                    const newData = addAction(mode, side);
                    onChangeData(newData);

                    const newChanges: HistorySlice = {
                        prevData: data,
                        newData: newData,
                    };
                    setCellChanges([...cellChanges.slice(0, cellChangesIndex + 1), newChanges]);
                    setCellChangesIndex(cellChangesIndex + 1);

                    const colCount = data[0].length;
                    const rowCount = data.length;
                    if (isSelectedCol && thisSelectedIndex === colCount - 1) {
                        setTimeout(() => {
                            scrollTo({ left: 10000 });
                        }, 20);
                    }
                    if (isSelectedRow && thisSelectedIndex === rowCount - 1) {
                        setTimeout(() => {
                            scrollTo({ top: 10000 });
                        }, 20);
                    }
                },
            };
        };

        return [
            getAddMenuOption(isSelectedRow ? 'row' : 'col', 'above'),
            getAddMenuOption(isSelectedRow ? 'row' : 'col', 'below'),
            {
                id: `delete-${isSelectedRow ? 'row' : 'col'}`,
                label: getMenuOptionNode({
                    iconName: 'TrashCan',
                    label: $t((v) => v.chartLayer.panel.delete),
                }),
                handler: () => {
                    if (isNaN(thisSelectedIndex)) return;

                    onAction?.({ target: 'updateRowOrColCount' });

                    const newData = cloneDeep(data);
                    if (isSelectedRow) {
                        newData.splice(thisSelectedIndex, 1);
                    } else {
                        newData.forEach((row) => {
                            row.splice(thisSelectedIndex, 1);
                        });
                    }
                    onChangeData(newData);

                    const newChanges: HistorySlice = {
                        prevData: data,
                        newData: newData,
                    };
                    setCellChanges([...cellChanges.slice(0, cellChangesIndex + 1), newChanges]);
                    setCellChangesIndex(cellChangesIndex + 1);
                },
            },
        ];
    };

    const onChangeColor = (index: number, color: string) => {
        const newColors = [...colors];
        newColors[index] = color;
        onChangeColors(newColors);
        onAction?.({ target: 'chartSerieColorChange', data: { isFromEditModal: true } });
    };

    const [cellChangesIndex, setCellChangesIndex] = useState(-1);
    type HistorySlice = CellChange<CustomCell>[] | { prevData: TableData; newData: TableData };
    const [cellChanges, setCellChanges] = useState<HistorySlice[]>(() => []);

    const applyNewValue = (changes: HistorySlice, prevData: TableData, usePrevValue: boolean = false): TableData => {
        if (Array.isArray(changes)) {
            const newData = cloneDeep(prevData);
            changes.forEach((change) => {
                const rowIndex = change.rowId;
                const colIndex = change.columnId;
                const cell = usePrevValue ? change.previousCell : change.newCell;
                newData[rowIndex][colIndex] = cell.bizValue;
            });
            return newData;
        }
        return cloneDeep(usePrevValue ? changes.prevData : changes.newData);
    };

    const undoChanges = (changes: HistorySlice, prevData: TableData): TableData => {
        const updated = applyNewValue(changes, prevData, true);
        setCellChangesIndex(cellChangesIndex - 1);
        return updated;
    };

    const redoChanges = (changes: HistorySlice, prevData: TableData): TableData => {
        const updated = applyNewValue(changes, prevData);
        setCellChangesIndex(cellChangesIndex + 1);
        return updated;
    };

    const handleUndoChanges = () => {
        if (cellChangesIndex >= 0) {
            const newData = undoChanges(cellChanges[cellChangesIndex], data);
            onChangeData(newData);
        }
    };

    const handleRedoChanges = () => {
        if (cellChangesIndex + 1 <= cellChanges.length - 1) {
            const newData = redoChanges(cellChanges[cellChangesIndex + 1], data);
            onChangeData(newData);
        }
    };

    return (
        <ScrollBars2 ref={scrollBars2Ref} scrollerProps={{ className: scrollerClassName }}>
            <div
                onKeyDown={(e) => {
                    if ((!clientInfo.isMac && e.ctrlKey) || e.metaKey) {
                        switch (e.key) {
                            case 'z':
                                handleUndoChanges();
                                return;
                            case 'y':
                                handleRedoChanges();
                                return;
                        }
                    }
                }}
            >
                <ReactGrid
                    ref={reactGridRef}
                    enableFillHandle
                    enableRangeSelection
                    enableFullWidthHeader
                    enableColumnResizeOnAllHeaders
                    enableColumnSelection
                    enableGroupIdRender
                    enableRowSelection
                    rows={[headerRow, ...rows]}
                    columns={columns}
                    onCellsChanged={(_changes) => {
                        const changes = _changes as any as CellChange<CustomCell>[];
                        handleChanges(changes);

                        setCellChanges([...cellChanges.slice(0, cellChangesIndex + 1), changes]);
                        setCellChangesIndex(cellChangesIndex + 1);
                    }}
                    onContextMenu={simpleHandleContextMenu}
                    customCellTemplates={{
                        header: new HeaderTemplate(),
                        empty: getCellTemplateInstance(),
                        'text-and-color': getCellTemplateInstance(),
                        text: getCellTemplateInstance(),
                        value: getCellTemplateInstance(),
                    }}
                    onColumnResized={(columnId, width) => {
                        setColWidthMap((prev) => ({ ...prev, [columnId]: width }));
                    }}
                    onSelectionChanging={([selectedRange]) => {
                        const rangeDom = document.querySelector<HTMLDivElement>('.rg-partial-area-selected-range');
                        if (!rangeDom) return true;
                        console.log('selectedRange', selectedRange);
                        if (
                            selectedRange.first.column.columnId === 'header' &&
                            selectedRange.first.row.rowId === 'header'
                        ) {
                            rangeDom.style.borderTopLeftRadius = '8px';
                        } else {
                            rangeDom.style.borderTopLeftRadius = '';
                        }
                        return true;
                    }}
                />
            </div>
        </ScrollBars2>
    );
};

Table.displayName = 'Table';

export default Table;
